import React from "react";
import { Helmet } from "react-helmet";
import { Button, Icon } from "armstrong-react";
import { contentDetail } from "@travellocal/utils/webRoutes"

import "./disqusComments.scss";

/**
 * Global variable added by Disqus embed.js
 */
let DISQUS;

interface Props {
  /**
   * `shortname` tells the Disqus service your forum's shortname,
   * which is the unique identifier for your website as registered
   * on Disqus. If undefined , the Disqus embed will not load.
   */
  shortname: string;

  /**
   * `identifier` tells the Disqus service how to identify the
   * current page. When the Disqus embed is loaded, the identifier
   * is used to look up the correct thread. If disqus_identifier
   * is undefined, the page's URL will be used. The URL can be
   * unreliable, such as when renaming an article slug or changing
   * domains, so we recommend using your own unique way of
   * identifying a thread.
   */
  identifier: string;

  /**
   * `title` tells the Disqus service the title of the current page.
   * This is used when creating the thread on Disqus for the first time.
   * If undefined, Disqus will use the <title> attribute of the page.
   * If that attribute could not be used, Disqus will use the URL of the page.
   */
  title: string;

  /**
   * `url` tells the Disqus service the URL of the current page.
   * If undefined, Disqus will take the window.location.href.
   * This URL is used to look up or create a thread if disqus_identifier
   * is undefined. In addition, this URL is always saved when a thread is
   * being created so that Disqus knows what page a thread belongs to.
   */
  url: string;

  /**
   * `category_id` tells the Disqus service the category to be used for
   * the current page. This is used when creating the thread on Disqus
   * for the first time.
   */
  category_id?: string;

  /**
   * `onNewComment` function accepts one parameter `comment` which is a
   * JavaScript object with comment `id` and `text`. This allows you to track
   * user comments and replies and run a script after a comment is posted.
   */
  onNewComment?: (comment: { id: string, text: string }) => void;

  className?: string;
}

interface State {
  loaded?: boolean;
}

/**
 * This component adapted from https://github.com/theplatapi/react-disqus-thread/blob/master/lib/components/DisqusThread.js
 * MIT Licence
 */
export class DisqusComments extends React.Component<Props, State> {
  private readonly PREFIX = 'disqus_';
  private disqus: HTMLScriptElement;

  public constructor(props: Props) {
    super(props);
    this.state = { loaded: false };
  }

  /**
   * Copy disqus variables to the browser context
   */
  private copyProps(context: Window, props: Props) {
    for (let key of Object.keys(props)) {
      context[this.PREFIX + key] = props[key];
    }

    if (typeof props.onNewComment === 'function') {
      context[this.PREFIX + 'config'] = function config() {
        this.callbacks.onNewComment = [
          function handleNewComment(comment) {
            props.onNewComment(comment);
          }
        ];
      };
    }
  }

  private getDisqusRoot = () => document.getElementsByTagName('body')[0];

  private loadDisqus() {
    let url = this.props.url && this.props.url.length ? this.props.url : window.location.href;

    // Can't rely on Disqus to actually call config on time, so always ensure these are set correctly
    this.copyProps(window, this.props);

    // If Disqus has already been added, reset it
    let DISQUS = window['DISQUS'];
    if (DISQUS != undefined) {
      DISQUS.reset({
        reload: true,
        config: () => {
          // Disqus needs hash to change after reset is called, see https://help.disqus.com/customer/portal/articles/472107
          // replaceState means back navigation will be unaffected
          // - just setting the hash adds an entry to the history
          history.replaceState(null, null, `#!newthread`);

          console.debug(`Disqus reloaded for ${this.props.identifier}`);
        }
      });
    }
    else { // Otherwise add Disqus to the page      
      const child = document.createElement('script');

      child.async = true;
      child.type = 'text/javascript';
      child.src = 'https://' + this.props.shortname + '.disqus.com/embed.js';

      this.disqus = child;

      this.getDisqusRoot().appendChild(child);
    }
  }

  private loadComments = () => {
    this.setState({ loaded: true }, () => this.loadDisqus());
  }

  public componentWillUnmount() {
    for (let key of Object.keys(this.props)) {
      let configKey = `${this.PREFIX}${key}`;
      if (window[configKey]) {
        window[configKey] = null;
      }
    }

    if (this.disqus) {
      this.getDisqusRoot().removeChild(this.disqus);
    }
  }

  public render() {
    let className = "disqus-comments";
    if (this.props.className) {
      className += ` ${this.props.className}`;
    }

    return (
      <div className={className}>
        {this.props.children}

        {!this.state.loaded && 
          <div className="not-loaded">
            <Button leftIcon={Icon.Icomoon.bubbles3} onClick={() => this.loadComments()}>
              Join the conversation
            </Button>
            <p>Click to see public comments for this article.</p>
          </div>
        }
        <div id="disqus_thread" />
      </div>
    );
  }
}

interface InspirationProps {
  content: IContent;
  className?: string;
}

/**
 * Lovely wrapper that converts inspiration content to disqus config
 */
export class InspirationDisqusComments extends React.Component<InspirationProps, {}> {
  private readonly forumName = "www-travellocal-com";

  public render() {
    let content = this.props.content;
    if (content == null) {
      return null;
    }

    let uniqueId = `travellocal-inspiration-${content.id}`; // this should always be the same to keep the comments
    let url = `${globalConfig.wwwBaseUrl}${contentDetail(content, false)}`;
    return (
      <DisqusComments className={this.props.className} shortname={this.forumName} identifier={uniqueId} url={url} title={content.title}>
        {this.props.children}
      </DisqusComments>
    );
  }
}
